// src/components/sections/AppSection.jsx
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import appbg from "../../assets/background/appbg.png";

// Images
import blockchain from "../../assets/img/blockchaincore.png";
import ssi from "../../assets/img/ssi.png";
import qes from "../../assets/img/qes.png";
import digiwallet from "../../assets/img/digitalwallet.png";
import intero from "../../assets/img/intero.png";

// Icons
import blockchainicon from "../../assets/icon/blockchain.png";
import ssiicon from "../../assets/icon/ssi.png";
import qesicon from "../../assets/icon/qes.png";
import digiwalleticon from "../../assets/icon/digiwallet.png";
import interoicon from "../../assets/icon/intero.png";

// Khóa các feature theo đúng thứ tự bạn muốn hiển thị
const FEATURE_KEYS = ["blockchain", "ssi", "qes", "wallet", "intero"];

export default function Solutions2Section() {
  const { t } = useTranslation();

  // Map key -> image/icon (không phụ thuộc ngôn ngữ)
  const IMAGE_MAP = useMemo(
    () => ({
      blockchain,
      ssi,
      qes,
      wallet: digiwallet,
      intero,
    }),
    []
  );

  const ICON_MAP = useMemo(
    () => ({
      blockchain: blockchainicon,
      ssi: ssiicon,
      qes: qesicon,
      wallet: digiwalleticon,
      intero: interoicon,
    }),
    []
  );

  // Build features từ i18n (title/desc/titlesub...)
  const features = useMemo(() => {
    return FEATURE_KEYS.map((key) => ({
      key,
      title: t(`app.features.${key}.title`),
      desc: t(`app.features.${key}.desc`, { returnObjects: true }),
      titlesub: t(`app.features.${key}.titlesub`),
      titlesub1: t(`app.features.${key}.titlesub1`),
      image: IMAGE_MAP[key],
      icon: ICON_MAP[key],
    }));
  }, [t, IMAGE_MAP, ICON_MAP]);

  // Lưu state theo "key" để không bị stale khi đổi ngôn ngữ
  const [activeKey, setActiveKey] = useState(FEATURE_KEYS[0]);
  const active = features.find((f) => f.key === activeKey) ?? features[0];

  return (
    <section
      id="solutions2"
      className="relative isolate text-white bg-cover bg-center bg-no-repeat px-4 py-36 mobile:py-10 tabletair:py-16 tabletmini:py-16"
      style={{ backgroundImage: `url(${appbg})` }}
    >
      <div className="max-w-[1400px] mx-auto flex flex-col gap-[48px] mobile:px-4 tabletpro:px-10 tabletair:px-10 tabletmini:px-10">
        {/* Headline */}
        <div className="flex flex-col gap-[16px] mx-auto">
          <p className="text-center font-medium text-[20px] mobile:text-[14px] font-bold text-brandBlue-500 tracking-wide uppercase">
            {t("app.caption", { defaultValue: "GIẢI PHÁP" })}
          </p>
          <h2 className="text-center font-semibold text-[56px] mobile:text-[24px] tabletpro:text-[56px] tabletair:text-[36px] tabletmini:text-[32px] text-brandBlue-900 font-md max-w-[1400px] mx-auto">
            {t("app.headline", {
              defaultValue: "Công nghệ cốt lõi của Quantera",
            })}
          </h2>
          <p className="text-center font-normal text-neutral-10 max-w-[1120px] mx-auto text-[16px] mobile:text-[16px] tabletpro:text-[16px] tabletair:text-[16px] tabletmini:text-[16px]">
            {t("app.sub", {
              defaultValue:
                "Nền tảng định danh số ứng dụng công nghệ blockchain Hyperledger Fabric, tích hợp các thành phần như SSI, ký số điện tử (QES), ví điện tử bảo mật và khả năng kết nối xuyên biên giới. Hệ thống hỗ trợ các chuẩn quốc tế (EBSI, EUDI Wallet, DIDComm), đảm bảo tự chủ danh tính, bảo vệ dữ liệu cá nhân và tương thích với hạ tầng định danh số toàn cầu.",
            })}
          </p>
        </div>

        {/* 2 columns */}
        <div className="grid tabletair:grid-cols-1 tabletmini:grid-cols-1 mobile:grid-cols-1 grid-cols-[3fr_2fr] tabletpro:grid-cols-2 gap-8 items-center">
          {/* Left side: clickable cards */}
          <div className="flex flex-col gap-4">
            {features.map((f) => (
              <button
                key={f.key}
                onClick={() => setActiveKey(f.key)}
                className={`flex items-center gap-4 text-left p-4 transition-all ${
                  activeKey === f.key
                    ? "border-l-[5px] border-l-brandBlue-500 text-white bg-[linear-gradient(to_right,#DEF1FF,#DFEFFF,#FFFFFF33)]"
                    : "text-neutral-200 hover:bg-[linear-gradient(to_right,#DEF1FF,#DFEFFF,#FFFFFF33)]"
                }`}
              >
                {/* Icon */}
                <img
                  src={f.icon}
                  alt={f.title}
                  className="w-24 h-24 mobile:w-16 mobile:h-16 flex-shrink-0 mt-1"
                />
                <div>
                  <h3 className="font-semibold text-neutral-13 text-[18px] mobile:text-[16px] mb-2">
                    {f.title}
                  </h3>
                  <ul className="list-disc list-outside marker:text-xs pl-6 text-neutral-10 font-normal text-[16px] mobile:text-[14px] space-y-1">
                    {Array.isArray(f.desc) &&
                      f.desc.map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                </div>
              </button>
            ))}
          </div>

          {/* Right side: image with overlay title */}
          <div className="relative mobile:ml-[7%] tabletmini:ml-[17%] tabletair:ml-[17%] w-[500px] h-[760px] tabletpro:w-[420px] tabletpro:h-[660px]  mobile:w-[280px] mobile:h-[440px]">
            {/* Ảnh nền */}
            <img
              src={active.image}
              alt={active.title}
              className="w-full h-full object-contain"
            />

            {/* Overlay gradient từ trắng sang trong suốt */}
            <div className="absolute inset-0 bg-gradient-to-t from-white via-white/30 to-transparent"></div>

            {/* Chữ nổi trên overlay */}
            <div className="relative top-[-35%] left-0 z-10 flex flex-col items-start text-brandBlue-500">
              <h3 className="font-manrope text-[72px] mobile:text-[40px] tabletpro:text-[56px] tabletair:text-[48px] tabletmini:text-[56px] font-extrabold">
                {active.titlesub}
              </h3>
              <h4 className="font-manrope text-[72px] mobile:text-[40px] tabletpro:text-[56px] tabletair:text-[48px] tabletmini:text-[56px] font-extrabold">
                {active.titlesub1}
              </h4>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
